import { Avatar, Box, HStack, Textarea, VStack } from '@chakra-ui/react';
import { useRoastCommentMutation } from 'hooks/mutations/roast/useRoastCommentMutation';
import { useGetRoastCommentsQuery } from 'hooks/queries/roast/useGetRoastCommentsQuery';
import { useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import Loader from 'components/Loader';
import Text from 'components/Text';

interface RoastCommentSectionProps {
  roastId: string;
  commentCount: number;
}

const MAX_COMMENT_LENGTH = 1000;

const RoastCommentSection = ({ roastId, commentCount }: RoastCommentSectionProps) => {
  const { id: userId } = useCurrentUser();
  const { data: comments, isPending } = useGetRoastCommentsQuery({ roastId });
  const { mutate: manageComment, isPending: isMutating } = useRoastCommentMutation();
  const [newComment, setNewComment] = useState('');

  const handleSubmit = () => {
    if (!newComment.trim()) return;
    manageComment(
      { action: 'add', roastId, content: newComment.trim() },
      {
        onSuccess: () => setNewComment(''),
      }
    );
  };

  const handleDelete = (commentId: string) => {
    manageComment({ action: 'delete', roastId, commentId });
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <VStack spacing={4} alignItems="flex-start" width="100%">
      <Text fontSize="medium" fontWeight="semibold">
        Comments ({commentCount})
      </Text>

      {/* Add comment form */}
      <VStack spacing={2} width="100%">
        <Textarea
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          maxLength={MAX_COMMENT_LENGTH}
          size="sm"
          rows={3}
          backgroundColor="whiteAlpha.50"
          borderColor="whiteAlpha.200"
          _hover={{ borderColor: 'whiteAlpha.300' }}
          _focus={{ borderColor: 'purple.400', boxShadow: '0 0 0 1px var(--chakra-colors-purple-400)' }}
        />
        <HStack width="100%" justifyContent="space-between">
          <Text fontSize="xMicro" opacity="tertiary">
            {newComment.length}/{MAX_COMMENT_LENGTH}
          </Text>
          <Button
            scheme="primary"
            size="sm"
            onClick={handleSubmit}
            isLoading={isMutating}
            isDisabled={!newComment.trim()}
          >
            Comment
          </Button>
        </HStack>
      </VStack>

      {/* Comments list */}
      {isPending ? (
        <Loader />
      ) : (
        <VStack spacing={3} width="100%">
          {comments && comments.length > 0 ? (
            comments.map((comment) => (
              <Box
                key={comment.id}
                width="100%"
                p={3}
                borderRadius="lg"
                backgroundColor="whiteAlpha.50"
                border="1px solid"
                borderColor="whiteAlpha.100"
              >
                <HStack spacing={2} mb={2} justifyContent="space-between">
                  <HStack spacing={2}>
                    <Box
                      as="a"
                      href={`/profile/${comment.screen_name}`}
                      display="flex"
                      alignItems="center"
                      gap="8px"
                      _hover={{ opacity: 0.8 }}
                      transition="opacity 0.15s"
                    >
                      <Avatar size="xs" name={comment.screen_name} src={comment.avatar_url} />
                      <Text fontSize="small" fontWeight="semibold" _hover={{ color: 'orange.400' }} transition="color 0.15s">
                        {comment.screen_name}
                      </Text>
                    </Box>
                    <Text fontSize="xMicro" opacity="tertiary">
                      {formatDate(comment.created_at)}
                    </Text>
                  </HStack>
                  {comment.user_id === userId && (
                    <Button
                      scheme="ghost"
                      size="xs"
                      onClick={() => handleDelete(comment.id)}
                      color="red.400"
                      _hover={{ color: 'red.300' }}
                    >
                      Delete
                    </Button>
                  )}
                </HStack>
                <Text fontSize="small" opacity="secondary" whiteSpace="pre-wrap">
                  {comment.content}
                </Text>
              </Box>
            ))
          ) : (
            <Text fontSize="small" opacity="tertiary">
              No comments yet. Be the first to share your thoughts!
            </Text>
          )}
        </VStack>
      )}
    </VStack>
  );
};

export default RoastCommentSection;
